import { z } from 'zod/v4';

import type { FormSpec, FieldSpec, FieldOption, FieldKind, FieldOverrideMap } from './form-spec';
import { getPathValue, setPathValue, deletePathValue } from './path-utils';
import { humanizeIdentifier } from '@/lib/string-format';

/**
 * Build form specs from Zod object schemas, providing a field list for rendering,
 * initial values, and payload normalization (parse + drop empty optional fields).
 */
/**
 * Convert schema field keys (camelCase, PascalCase, snake_case) into readable labels.
 * Used as a default label when no explicit override is provided.
 */
function labelize(value: string): string {
    return humanizeIdentifier(value, { casing: 'sentence' });
}

type AnySchema = z.ZodTypeAny | z.core.$ZodType;

/**
 * Peel off optional/nullable/default wrappers to get the base schema and flags.
 */
function unwrapSchema(schema: AnySchema) {
    let current = schema as unknown as z.ZodTypeAny;
    let optional = false;
    let nullable = false;

    while (true) {
        if (current instanceof z.ZodOptional) {
            optional = true;
            current = current.unwrap() as unknown as z.ZodTypeAny;
            continue;
        }
        if (current instanceof z.ZodNullable) {
            nullable = true;
            current = current.unwrap() as unknown as z.ZodTypeAny;
            continue;
        }
        if (current instanceof z.ZodDefault) {
            optional = true;
            current = current.unwrap() as unknown as z.ZodTypeAny;
            continue;
        }
        // kubb (zod 4) emits nullable scalars as `z.union([T, z.null()])` rather than `T.nullable()`.
        // Collapse that shape to T so enum/array fields still resolve their kind + options.
        if (current instanceof z.ZodUnion) {
            const members = current.options as unknown as z.ZodTypeAny[];
            const nonNull = members.filter((member) => !(member instanceof z.ZodNull));
            if (nonNull.length === 1 && nonNull.length < members.length) {
                nullable = true;
                current = nonNull[0];
                continue;
            }
        }
        break;
    }

    return { schema: current as z.ZodTypeAny, optional, nullable };
}

/**
 * Convert enum values into select options with human-readable labels.
 */
function enumOptions(schema: z.ZodEnum): FieldOption[] {
    return schema.options.map((value) => ({ value: String(value), label: labelize(String(value)) }));
}

function isYesNoRadioEnum(schema: z.ZodEnum): boolean {
    const values = new Set(schema.options.map((value) => String(value)));
    if (!values.has('Yes') || !values.has('No')) return false;
    if (values.size === 2) return true;
    return values.size === 3 && values.has('CannotSpecify');
}

/**
 * Infer a basic field kind from a Zod schema and the field key name.
 */
function inferKind(schema: z.ZodTypeAny, key: string): FieldKind {
    if (schema instanceof z.ZodISODateTime) return 'datetime';
    if (schema instanceof z.ZodBoolean) return 'checkbox';
    if (schema instanceof z.ZodNumber) return 'number';
    if (schema instanceof z.ZodEnum) {
        if (isYesNoRadioEnum(schema)) return 'radio';
        return 'select';
    }
    if (schema instanceof z.ZodArray) {
        const { schema: element } = unwrapSchema(schema.element);
        if (element instanceof z.ZodEnum) return 'multiselect';
        return 'multiselect';
    }

    if (schema instanceof z.ZodString) {
        const lowered = key.toLowerCase();
        if (lowered.includes('description') || lowered.includes('explanation')) {
            return 'textarea';
        }
        return 'text';
    }

    return 'text';
}

/**
 * Provide a default parse function for specific field kinds.
 */
function defaultParse(kind: FieldKind) {
    if (kind === 'datetime') {
        return (value: unknown) => {
            if (!value || typeof value !== 'string') return value;
            const parsed = new Date(value);
            return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
        };
    }
    return undefined;
}

/**
 * Flatten a nested Zod object schema into a list of FieldSpec entries.
 */
function buildFields(schema: z.ZodObject, path: string[] = []): FieldSpec[] {
    const rawShape = schema.shape as z.core.$ZodShape | (() => z.core.$ZodShape);
    const shape = typeof rawShape === 'function' ? rawShape() : rawShape;

    return Object.entries(shape).flatMap(([key, prop]) => {
        const { schema: unwrapped, optional, nullable } = unwrapSchema(prop as AnySchema);
        const fieldPath = [...path, key];

        if (unwrapped instanceof z.ZodObject) {
            return buildFields(unwrapped, fieldPath);
        }

        const kind = inferKind(unwrapped, key);
        const required = !(optional || nullable);
        const options =
            unwrapped instanceof z.ZodEnum
                ? enumOptions(unwrapped)
                : unwrapped instanceof z.ZodArray
                  ? (() => {
                        const { schema: element } = unwrapSchema(unwrapped.element);
                        if (element instanceof z.ZodEnum) {
                            return enumOptions(element);
                        }
                        return undefined;
                    })()
                  : undefined;
        const maxLength = unwrapped instanceof z.ZodString ? (unwrapped.maxLength ?? undefined) : undefined;

        return [
            {
                key: fieldPath.join('.'),
                path: fieldPath,
                label: labelize(key),
                kind,
                required,
                nullable,
                options,
                ...(maxLength === undefined ? {} : { maxLength }),
                parse: defaultParse(kind),
            },
        ];
    });
}

/**
 * Build a FormSpec from a Zod object schema, ready for rendering.
 */
export function buildFormSpec(schema: z.ZodTypeAny, incidentType?: FormSpec['incidentType']): FormSpec {
    if (!(schema instanceof z.ZodObject)) {
        throw new Error('buildFormSpec expects a ZodObject schema.');
    }

    return {
        incidentType,
        fields: buildFields(schema),
    };
}

/**
 * Apply field-level overrides (label, kind, hidden, parse, etc.) to a FormSpec.
 */
export function applyFormOverrides(spec: FormSpec, overrides: FieldOverrideMap = {}): FormSpec {
    return {
        ...spec,
        fields: spec.fields.map((field) => {
            const override = overrides[field.key];
            if (!override) return field;
            return {
                ...field,
                ...override,
                path: override.path ?? field.path,
                key: override.key ?? field.key,
            };
        }),
    };
}

/**
 * Create initial form values based on the inferred field kinds.
 */
export function buildInitialValues(spec: FormSpec) {
    const values: Record<string, unknown> = {};
    for (const field of spec.fields) {
        let initial: unknown = '';
        if (field.kind === 'checkbox') initial = false;
        if (field.kind === 'multiselect') initial = [];
        if (field.kind === 'number') initial = undefined;
        if (field.kind === 'file') initial = undefined;
        setPathValue(values, field.path, initial);
    }
    return values;
}

/**
 * Normalize form values for submission: apply parsers and drop empty optional fields.
 */
export function normalizePayload(values: Record<string, unknown>, spec: FormSpec) {
    const payload = JSON.parse(JSON.stringify(values)) as Record<string, unknown>;

    for (const field of spec.fields) {
        // `hidden` only suppresses rendering, not payload inclusion — fields like
        // `location.sites` are populated programmatically from another field's value.
        if (field.visibleWhen && !field.visibleWhen(values)) {
            deletePathValue(payload, field.path);
            continue;
        }
        const raw = getPathValue(payload, field.path);
        const parsed = field.parse ? field.parse(raw) : raw;
        setPathValue(payload, field.path, parsed);

        const isRequired = field.required || Boolean(field.requiredWhen?.(values));
        if (!isRequired) {
            if (parsed === '' || parsed === undefined || parsed === null) {
                deletePathValue(payload, field.path);
            } else if (Array.isArray(parsed) && parsed.length === 0) {
                deletePathValue(payload, field.path);
            }
        }
    }

    return payload;
}

export { labelize };
